import { AnimatePresence, motion } from "framer-motion";
import { Gift, ShoppingBag, Trash2, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import { formatINR } from "../lib/api";

export default function CartDrawer() {
  const {
    items,
    isCartOpen,
    setCartOpen,
    setCheckoutOpen,
    setQty,
    remove,
    count,
    subtotal,
    savings,
    packingFee,
    total,
    freePackingAbove,
  } = useCart();

  const remaining = freePackingAbove - subtotal;

  return (
    <AnimatePresence>
      {isCartOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setCartOpen(false)}
            className="fixed inset-0 z-[60] bg-obsidian/75 backdrop-blur-sm"
            data-testid="cart-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 320, damping: 34 }}
            className="fixed right-0 top-0 z-[61] flex h-full w-full max-w-md flex-col border-l border-ember/15 bg-obsidian"
            data-testid="cart-drawer"
            data-lenis-prevent
          >
            <div className="flex items-center justify-between border-b border-starlight/10 p-5">
              <div>
                <h3 className="font-display text-xl font-bold text-starlight">Your Cracker Box</h3>
                <p className="font-mono text-[11px] text-muted-night mt-0.5">
                  {count} item{count === 1 ? "" : "s"} inside
                </p>
              </div>
              <button
                data-testid="cart-close-button"
                onClick={() => setCartOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-starlight/15 text-muted-night hover:border-ember/60 hover:text-ember transition-colors"
                aria-label="Close cart"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full border border-ember/30 bg-ember/10">
                  <ShoppingBag className="h-7 w-7 text-ember" />
                </span>
                <p className="font-display text-xl text-starlight">Your box is empty</p>
                <p className="max-w-xs text-sm text-muted-night">
                  Add some sparkle from the collection — sparklers, rockets, sky shows and more.
                </p>
                <button
                  data-testid="cart-empty-browse-button"
                  onClick={() => setCartOpen(false)}
                  className="mt-2 rounded-full bg-ember px-7 py-3 font-mono text-[11px] font-bold tracking-[0.2em] uppercase text-obsidian hover:bg-marigold transition-colors"
                >
                  Browse crackers
                </button>
              </div>
            ) : (
              <>
                <div className="flex-1 space-y-3 overflow-y-auto p-5">
                  {items.map(({ product, qty }) => (
                    <div
                      key={product.id}
                      data-testid={`cart-item-${product.id}`}
                      className="flex gap-3 rounded-2xl border border-starlight/10 bg-card p-3"
                    >
                      <img
                        src={product.image}
                        alt={product.name}
                        className="h-16 w-16 shrink-0 rounded-xl object-cover"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-starlight">{product.name}</p>
                        <p className="font-mono text-[10px] text-muted-night">{product.content}</p>
                        <div className="mt-2 flex items-center justify-between">
                          <div className="flex items-center gap-2 rounded-full border border-starlight/15 px-1">
                            <button
                              data-testid={`cart-qty-decrease-${product.id}`}
                              onClick={() => setQty(product.id, qty - 1)}
                              className="flex h-6 w-6 items-center justify-center text-muted-night hover:text-ember"
                              aria-label={`Decrease ${product.name}`}
                            >
                              −
                            </button>
                            <span className="font-mono text-xs text-starlight">{qty}</span>
                            <button
                              data-testid={`cart-qty-increase-${product.id}`}
                              onClick={() => setQty(product.id, qty + 1)}
                              className="flex h-6 w-6 items-center justify-center text-muted-night hover:text-ember"
                              aria-label={`Increase ${product.name}`}
                            >
                              +
                            </button>
                          </div>
                          <p className="font-mono text-sm font-bold text-ember">{formatINR(product.price * qty)}</p>
                        </div>
                      </div>
                      <button
                        data-testid={`cart-remove-${product.id}`}
                        onClick={() => remove(product.id)}
                        className="self-start text-muted-night/60 hover:text-crimson transition-colors"
                        aria-label={`Remove ${product.name}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="border-t border-starlight/10 p-5 space-y-4">
                  <div className="rounded-2xl border border-ember/25 bg-ember/5 p-3.5">
                    {remaining > 0 ? (
                      <>
                        <p className="flex items-center gap-2 font-mono text-[11px] tracking-[0.14em] uppercase text-muted-night">
                          <Gift className="h-3.5 w-3.5 text-ember" />
                          {formatINR(remaining)} away from a free gift
                        </p>
                        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-starlight/10">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-crimson to-ember transition-all duration-500"
                            style={{ width: `${Math.min(100, (subtotal / freePackingAbove) * 100)}%` }}
                          />
                        </div>
                      </>
                    ) : (
                      <p className="flex items-center gap-2 font-mono text-[11px] tracking-[0.14em] uppercase text-discount-green">
                        <Gift className="h-3.5 w-3.5" /> Free gift + free packing unlocked
                      </p>
                    )}
                  </div>

                  <div className="space-y-1.5 font-mono text-xs">
                    <div className="flex justify-between text-muted-night">
                      <span>Subtotal</span>
                      <span className="text-starlight">{formatINR(subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-muted-night">
                      <span>Your savings</span>
                      <span className="text-discount-green">− {formatINR(savings)}</span>
                    </div>
                    <div className="flex justify-between text-muted-night">
                      <span>Packing & handling</span>
                      <span className={packingFee === 0 ? "text-discount-green" : "text-starlight"}>
                        {packingFee === 0 ? "FREE" : formatINR(packingFee)}
                      </span>
                    </div>
                    <div className="flex justify-between border-t border-starlight/10 pt-2.5 text-sm font-bold">
                      <span className="text-starlight">Total (COD)</span>
                      <span className="text-ember" data-testid="cart-total">{formatINR(total)}</span>
                    </div>
                  </div>

                  <button
                    data-testid="proceed-to-checkout-button"
                    onClick={() => {
                      setCartOpen(false);
                      setCheckoutOpen(true);
                    }}
                    className="w-full rounded-full bg-ember py-4 font-mono text-xs font-bold tracking-[0.22em] uppercase text-obsidian transition-all duration-300 hover:bg-marigold hover:scale-[1.01]"
                  >
                    Place COD order
                  </button>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
